import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Send, ArrowLeft, Ban, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
function wsUrl() {
  const base = (BACKEND_URL || "").replace(/^http/, "ws");
  return `${base}/api/ws/match`;
}

export default function ConnectionRoom() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  const intentionalClose = useRef(false);
  const reconnectTimer = useRef(null);
  const draftKey = `maziara_conn_draft_${matchId}`;

  const [status, setStatus] = useState("connecting"); // connecting|open|reconnecting|ended|notfound
  const [meta, setMeta] = useState({ alias: "", language: "", can_send: false });
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState(() => (typeof window !== "undefined" && localStorage.getItem(draftKey)) || "");
  const [partnerOnline, setPartnerOnline] = useState(false);
  const [partnerTyping, setPartnerTyping] = useState(false);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages, partnerTyping]);
  // Preserve the draft across disconnects/reloads.
  useEffect(() => {
    if (text) localStorage.setItem(draftKey, text);
    else localStorage.removeItem(draftKey);
  }, [text, draftKey]);

  const send = useCallback((obj) => { if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(obj)); }, []);

  const connect = useCallback(() => {
    const ws = new WebSocket(wsUrl());
    wsRef.current = ws;
    ws.onopen = () => ws.send(JSON.stringify({ action: "open_connection", match_id: matchId }));
    ws.onmessage = (e) => {
      const m = JSON.parse(e.data);
      switch (m.type) {
        case "connection_opened":
          setMeta({ alias: m.alias, language: m.language, can_send: m.can_send });
          setMessages((m.messages || []).map((x) => ({ id: x.message_id, from: x.from, content: x.content })));
          setStatus("open"); break;
        case "message":
          setPartnerTyping(false);
          setMessages((prev) => prev.some((p) => p.id === m.message_id) ? prev : [...prev, { id: m.message_id, from: m.from, content: m.content }]); break;
        case "typing": setPartnerTyping(true); setTimeout(() => setPartnerTyping(false), 2500); break;
        case "partner_online": setPartnerOnline(true); break;
        case "partner_offline": setPartnerOnline(false); break;
        case "match_limit":
          if (m.reason === "membership_required") { setMeta((x) => ({ ...x, can_send: false })); toast.error("Become a member to keep messaging your connections."); }
          break;
        case "message_blocked": toast.error(m.reason); break;
        case "connection_ended": intentionalClose.current = true; setStatus("ended"); break;
        case "error": if (m.reason === "connection_not_found") { intentionalClose.current = true; setStatus("notfound"); } break;
        default: break;
      }
    };
    ws.onerror = () => {};
    ws.onclose = () => {
      if (intentionalClose.current) return;
      // Unexpected drop: keep the draft, show reconnecting, auto-retry.
      setStatus((s) => (s === "ended" || s === "notfound") ? s : "reconnecting");
      reconnectTimer.current = setTimeout(() => connect(), 2000);
    };
  }, [matchId]);

  useEffect(() => {
    connect();
    return () => {
      intentionalClose.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      try { wsRef.current?.send(JSON.stringify({ action: "leave_connection" })); } catch { /* closing */ }
      wsRef.current?.close();
    };
  }, [connect]);

  const sendMsg = () => {
    const t = text.trim();
    if (!t || !meta.can_send) return;
    if (wsRef.current?.readyState !== 1) { toast.error("Reconnecting… your message is saved, try again in a moment."); return; }
    send({ action: "message", content: t, client_id: `${Date.now()}` });
    setText("");
  };
  const disconnect = () => { send({ action: "disconnect", match_id: matchId }); };

  if (status === "connecting") return <div className="flex min-h-screen items-center justify-center bg-[#0A0908]"><Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" /></div>;
  if (status === "notfound") return <Centered title="Connection not found" onBack={() => navigate("/app/chats")} />;
  if (status === "ended") return <Centered title="This connection has ended" onBack={() => navigate("/app/chats")} />;

  return (
    <div className="flex h-screen flex-col" data-testid="connection-room">
      {status === "reconnecting" && (
        <div data-testid="ws-reconnecting" className="flex items-center justify-center gap-2 bg-[#211B12] px-4 py-2 text-xs text-[#E5C158]">
          <RefreshCw className="h-3 w-3 animate-spin" /> Connection lost — reconnecting… your draft is saved.
        </div>
      )}
      <header className="flex items-center gap-3 border-b border-[#2B241B] bg-[#12100E] px-4 py-3">
        <button onClick={() => navigate("/app/chats")} className="text-[#C8C2B5]"><ArrowLeft className="h-5 w-5" /></button>
        <div className="flex-1">
          <div className="flex items-center gap-2"><span className="font-semibold text-[#F4F1EA]">{meta.alias}</span>
            <span className="rounded-full bg-[#12281a] px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#9FE0B0]">Real person</span></div>
          <p className="text-xs text-[#8C8578]">{partnerOnline ? "online" : meta.language}</p>
        </div>
        <button title="Disconnect" onClick={disconnect} className="flex h-8 w-8 items-center justify-center rounded-full text-[#c98a8a] hover:bg-[#361217]/40"><Ban className="h-4 w-4" /></button>
      </header>

      <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto px-4 py-4">
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.from === "me" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${m.from === "me" ? "bg-[#D4AF37] text-[#0A0908]" : "bg-[#1E1B16] text-[#F4F1EA]"}`}>{m.content}</div>
          </div>
        ))}
        {partnerTyping && <p className="text-xs text-[#8C8578]">typing…</p>}
      </div>

      {meta.can_send ? (
        <div className="flex items-center gap-2 border-t border-[#2B241B] bg-[#12100E] px-3 py-3 safe-bottom">
          <input data-testid="connection-input" value={text} onChange={(e) => { setText(e.target.value); send({ action: "typing" }); }}
            onKeyDown={(e) => e.key === "Enter" && sendMsg()} placeholder="Message your connection…"
            className="flex-1 rounded-full border border-[#2B241B] bg-[#1A1714] px-4 py-2.5 text-sm text-[#F4F1EA] outline-none focus:border-[#D4AF37]/50" />
          <button data-testid="connection-send" onClick={sendMsg} className="flex h-10 w-10 items-center justify-center rounded-full bg-[#D4AF37] text-[#0A0908]"><Send className="h-4 w-4" /></button>
        </div>
      ) : (
        <div className="border-t border-[#2B241B] bg-[#17130D] px-4 py-3 text-center" data-testid="connection-readonly">
          <p className="text-sm text-[#C8C2B5]">Your membership is needed to keep messaging saved connections.</p>
          <button onClick={() => navigate("/app/membership")} className="mt-2 rounded-full bg-[#D4AF37] px-6 py-2 text-sm font-semibold text-[#0A0908]">Become a member</button>
        </div>
      )}
    </div>
  );
}

function Centered({ title, onBack }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#0A0908] bg-noise px-5 text-center">
      <p className="font-display text-xl text-[#F4F1EA]">{title}</p>
      <button onClick={onBack} className="mt-6 rounded-full border border-[#3D3428] px-6 py-3 text-sm text-[#F4F1EA]">Back to Chats</button>
    </div>
  );
}
